/**
 * Accessibility utilities for the One80Labs UI Component Library
 * These utilities help ensure components meet WCAG guidelines
 */

import React from 'react';

/**
 * Generates a unique ID for accessibility purposes
 * @param prefix - Optional prefix for the ID
 * @returns A unique ID string
 */
export function generateId(prefix = 'ui'): string {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Handles keyboard navigation for a list of focusable elements
 * @param event - The keyboard event
 * @param items - Array of focusable elements
 * @param currentIndex - Current focused index
 * @param onIndexChange - Callback when index changes
 */
export function handleKeyboardNavigation(
  event: React.KeyboardEvent,
  items: HTMLElement[],
  currentIndex: number,
  onIndexChange: (index: number) => void
): void {
  const { key } = event;
  
  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight':
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % items.length;
      onIndexChange(nextIndex);
      items[nextIndex]?.focus();
      break;
      
    case 'ArrowUp':
    case 'ArrowLeft':
      event.preventDefault();
      const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
      onIndexChange(prevIndex);
      items[prevIndex]?.focus();
      break;
      
    case 'Home':
      event.preventDefault();
      onIndexChange(0);
      items[0]?.focus();
      break;
      
    case 'End':
      event.preventDefault();
      const lastIndex = items.length - 1;
      onIndexChange(lastIndex);
      items[lastIndex]?.focus();
      break;
  }
}

/**
 * Manages focus trap for modal and dropdown components
 * @param containerRef - Reference to the container element
 * @param isActive - Whether the focus trap should be active
 */
export function useFocusTrap(containerRef: React.RefObject<HTMLElement>, isActive: boolean): void {
  React.useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown);
    firstElement?.focus();

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, containerRef]);
}

/**
 * Provides ARIA live region updates for screen readers
 * @param message - The message to announce
 * @param priority - The priority level ('polite' or 'assertive')
 */
export function announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove the announcement after it's been read
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

/**
 * Validates ARIA attributes for accessibility compliance
 * @param props - Component props to validate
 * @returns Array of validation errors
 */
export function validateAriaAttributes(props: Record<string, any>): string[] {
  const errors: string[] = [];

  // Check for required ARIA attributes
  if (props['aria-expanded'] !== undefined && props['aria-controls'] === undefined) {
    errors.push('aria-expanded requires aria-controls to be specified');
  }

  if (props['aria-haspopup'] !== undefined && props['aria-expanded'] === undefined) {
    errors.push('aria-haspopup requires aria-expanded to be specified');
  }

  if (props['aria-describedby'] !== undefined && !document.getElementById(props['aria-describedby'])) {
    errors.push(`aria-describedby references non-existent element: ${props['aria-describedby']}`);
  }

  if (props['aria-labelledby'] !== undefined && !document.getElementById(props['aria-labelledby'])) {
    errors.push(`aria-labelledby references non-existent element: ${props['aria-labelledby']}`);
  }

  return errors;
}

/**
 * Provides keyboard event handlers for interactive components
 * @param onEnter - Handler for Enter key
 * @param onSpace - Handler for Space key
 * @param onEscape - Handler for Escape key
 * @returns Object with keyboard event handlers
 */
export function useKeyboardHandlers(
  onEnter?: () => void,
  onSpace?: () => void,
  onEscape?: () => void
) {
  const handleKeyDown = React.useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        if (onEnter) {
          event.preventDefault();
          onEnter();
        }
        break;
      case ' ':
        if (onSpace) {
          event.preventDefault();
          onSpace();
        }
        break;
      case 'Escape':
        if (onEscape) {
          event.preventDefault();
          onEscape();
        }
        break;
    }
  }, [onEnter, onSpace, onEscape]);

  return { handleKeyDown };
}

/**
 * Hook for managing focus state
 * @param initialFocus - Whether to focus on mount
 * @returns Object with focus state and handlers
 */
export function useFocusManagement(initialFocus = false) {
  const [isFocused, setIsFocused] = React.useState(initialFocus);
  const elementRef = React.useRef<HTMLElement>(null);

  const handleFocus = React.useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = React.useCallback(() => {
    setIsFocused(false);
  }, []);

  const focus = React.useCallback(() => {
    elementRef.current?.focus();
  }, []);

  const blur = React.useCallback(() => {
    elementRef.current?.blur();
  }, []);

  React.useEffect(() => {
    if (initialFocus) {
      elementRef.current?.focus();
    }
  }, [initialFocus]);

  return {
    isFocused,
    elementRef,
    handleFocus,
    handleBlur,
    focus,
    blur,
  };
}

/**
 * Provides skip link functionality for keyboard navigation
 * @param targetId - ID of the target element to skip to
 * @param label - Label for the skip link
 * @returns Skip link component
 */
export function SkipLink({ targetId, label = 'Skip to main content' }: { targetId: string; label?: string }) {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      announceToScreenReader(`Skipped to ${label}`);
    }
  };

  return React.createElement('a', {
    href: `#${targetId}`,
    onClick: handleClick,
    className: "sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-white focus:rounded"
  }, label);
}

/**
 * Validates color contrast ratios for accessibility
 * @param foreground - Foreground color (hex)
 * @param background - Background color (hex)
 * @returns Object with contrast ratio and WCAG compliance
 */
export function validateColorContrast(foreground: string, background: string) {
  // Convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);

  if (!fg || !bg) {
    return { ratio: 0, compliant: false, errors: ['Invalid color format'] };
  }

  // Calculate relative luminance
  const getLuminance = (r: number, g: number, b: number) => {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  const l1 = getLuminance(fg.r, fg.g, fg.b);
  const l2 = getLuminance(bg.r, bg.g, bg.b);

  const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);

  return {
    ratio: Math.round(ratio * 100) / 100,
    compliant: {
      AA: ratio >= 4.5,
      'AA-Large': ratio >= 3,
      AAA: ratio >= 7,
      'AAA-Large': ratio >= 4.5
    }
  };
} 